import { alpha, Box, Stack } from "@mui/material";
import { type SupportArticle } from "@src/appV2/HelpCenter/types";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";

import { Icon } from "../../components/Icon";
import { Text } from "../../components/Text";
import {
  getBackgroundMuted,
  getBackgroundStandout,
  getBorderBase,
  getMutedText,
  getStrongText,
} from "../themeHelpers";
import { type HelpCenterCardTone } from "./HelpCenterActionCard";

interface SupportArticleCardProps {
  isEmbedded?: boolean;
  isLast?: boolean;
  supportArticle: SupportArticle;
  tone?: HelpCenterCardTone;
}

export function SupportArticleCard(props: SupportArticleCardProps) {
  const { isEmbedded = false, isLast = false, supportArticle, tone = "light" } = props;
  const isDark = tone === "dark";

  return (
    <Box
      component="a"
      href={supportArticle.attributes.distributionUrl}
      rel="noopener noreferrer"
      target="_blank"
      sx={(theme) => ({
        display: "block",
        width: "100%",
        textDecoration: "none",
        borderRadius: isEmbedded ? 0 : theme.borderRadius?.xLarge,
        border: isEmbedded ? "none" : `1px solid ${getBorderBase(theme)}`,
        borderBottom: isEmbedded && !isLast ? `1px solid ${getBorderBase(theme)}` : undefined,
        backgroundColor: "transparent",
        paddingTop: theme.spacing(3),
        paddingRight: theme.spacing(3),
        paddingBottom: theme.spacing(3),
        paddingLeft: theme.spacing(3),
        boxShadow: isEmbedded ? "none" : `0 0.5rem 1.5rem ${alpha(getStrongText(theme), 0.03)}`,
        transition: theme.transitions.create(
          ["border-color", "transform", "box-shadow", "background-color"],
          { duration: theme.transitions.duration.shorter }
        ),
        "&:hover": {
          backgroundColor: isEmbedded ? getBackgroundMuted(theme) : getBackgroundStandout(theme),
          transform: isEmbedded ? "none" : "translateY(-1px)",
          boxShadow: isEmbedded ? "none" : `0 0.75rem 1.75rem ${alpha(getStrongText(theme), 0.05)}`,
        },
        "&:focus-visible": {
          outline: `2px solid ${alpha(theme.palette.primary.main, 0.6)}`,
          outlineOffset: "2px",
        },
      })}
      onClick={() => {
        logEvent(APP_V2_USER_EVENTS.HELP_CENTER_ARTICLE_OPENED, {
          id: supportArticle.id,
          title: supportArticle.attributes.title,
          category: supportArticle.attributes.category,
        });
      }}
    >
      <Stack alignItems="center" direction="row" spacing={2}>
        <Box
          sx={(theme) => ({
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "2.25rem",
            height: "2.25rem",
            minWidth: "2.25rem",
            borderRadius: theme.borderRadius?.large,
            backgroundColor: getBackgroundMuted(theme),
            border: `1px solid ${alpha(getBorderBase(theme), isDark ? 0.85 : 1)}`,
          })}
        >
          <Icon size="small" sx={(theme) => ({ color: getStrongText(theme) })} type="documents" />
        </Box>

        <Stack sx={{ flex: 1, minWidth: 0, gap: "0.25rem" }}>
          <Text
            semibold
            variant="body2"
            sx={(theme) => ({
              color: theme.palette.text.primary,
            })}
          >
            {supportArticle.attributes.title}
          </Text>
          <Text variant="caption" sx={(theme) => ({ color: getMutedText(theme) })}>
            {supportArticle.attributes.category}
          </Text>
        </Stack>

        <Icon size="small" sx={(theme) => ({ color: getMutedText(theme) })} type="chevron-right" />
      </Stack>
    </Box>
  );
}
